import { useState, useRef, useEffect } from "react";

const GREEN      = "#4CC922";
const GREEN_DARK = "#3AAA18";
const DANGER     = "#E53E3E";
const BG         = "#F5F7F5";
const SURFACE    = "#FFFFFF";
const SURFACE2   = "#F5F7F5";
const BORDER     = "rgba(0,0,0,0.07)";
const TEXT_PRI   = "#1A1A1A";
const TEXT_SEC   = "#888888";
const TEXT_MUT   = "#AAAAAA";
const FONT       = "'Tajawal', sans-serif";
const API_URL    = "https://nebbi.manassat.co/api";
const LS_KEY     = "nebbi_user";
const LS_SKIP    = "nebbi_skipped";
const LS_HELP    = "nebbi_helped";

const CATEGORIES = [
  { name:"تبرع بالدم",    emoji:"🩸", color:"#E53E3E", bg:"#FFF5F5" },
  { name:"تطوع",          emoji:"🤝", color:"#3AAA18", bg:"#F0FAF0" },
  { name:"تبرع",          emoji:"📦", color:"#D97706", bg:"#FFFBEB" },
  { name:"تدريس",         emoji:"📚", color:"#7C3AED", bg:"#F5F3FF" },
  { name:"مساعدة غذائية", emoji:"🍱", color:"#EA580C", bg:"#FFF7ED" },
  { name:"أخرى",          emoji:"💡", color:"#0284C7", bg:"#F0F9FF" },
];
const URGENCIES = ["🔴 عاجل","🟡 هذا الأسبوع","🟠 هذا الشهر","🟢 مستمر"];

const SEED = [];

function timeAgo(d) {
  const s = Math.floor((Date.now() - new Date(d)) / 1000);
  if (s < 60) return "الآن";
  if (s < 3600) return `قبل ${Math.floor(s/60)} دقيقة`;
  if (s < 86400) return `قبل ${Math.floor(s/3600)} ساعة`;
  return `قبل ${Math.floor(s/86400)} يوم`;
}

function Badge({ color, bg, children }) {
  return <span style={{ background:bg, color, border:`1px solid ${color}25`, padding:"4px 12px", borderRadius:20, fontSize:12, fontWeight:700, display:"inline-block", fontFamily:FONT }}>{children}</span>;
}

function Pill({ children }) {
  return <span style={{ background:SURFACE2, color:TEXT_SEC, border:`1px solid ${BORDER}`, padding:"4px 10px", borderRadius:20, fontSize:11.5, fontWeight:600, fontFamily:FONT }}>{children}</span>;
}

function CardContent({ card }) {
  return (
    <div style={{ background:SURFACE, height:"100%", display:"flex", flexDirection:"column", borderRadius:20 }}>
      <div style={{ background:card.bg, padding:"clamp(20px,4.5vh,44px) 24px clamp(16px,3vh,28px)", textAlign:"center", borderBottom:`1px solid ${BORDER}`, flexShrink:0, borderRadius:"20px 20px 0 0" }}>
        <div style={{ fontSize:"clamp(48px,10vh,72px)", marginBottom:12, lineHeight:1 }}>{card.emoji}</div>
        <Badge color={card.color} bg={card.bg}>{card.category}</Badge>
      </div>
      <div style={{ padding:"20px 22px 8px", flex:1, minHeight:0, overflowY:"auto" }}>
        <h2 style={{ color:TEXT_PRI, fontSize:"clamp(17px,2.4vh,21px)", fontWeight:800, marginBottom:10, lineHeight:1.35, fontFamily:FONT }}>{card.title}</h2>
        <p style={{ color:TEXT_SEC, fontSize:"clamp(13px,1.8vh,14.5px)", lineHeight:1.75, marginBottom:18, fontFamily:FONT }}>{card.description}</p>
        <div style={{ display:"flex", gap:7, flexWrap:"wrap" }}>
          <Pill>📍 {card.location}</Pill>
          <Pill>{card.urgency}</Pill>
          <Pill>🕐 {card.posted || timeAgo(card.created_at)}</Pill>
        </div>
      </div>
      <div style={{ padding:"10px 22px", display:"flex", justifyContent:"space-between", borderTop:`1px solid ${BORDER}`, flexShrink:0 }}>
        <span style={{ color:GREEN, fontSize:12.5, fontFamily:FONT }}>👈 مساعدة</span>
        <span style={{ color:DANGER, fontSize:12.5, opacity:0.75, fontFamily:FONT }}>تخطّي 👉</span>
      </div>
    </div>
  );
}

export default function Nebbi() {
  const [view, setView]         = useState("feed");
  const [dbNeeds, setDbNeeds]   = useState([]);
  const [loading, setLoading]   = useState(true);
  const [idx, setIdx]           = useState(0);
  const [offset, setOffset]     = useState({ x:0, y:0 });
  const [dragging, setDragging] = useState(false);
  const [anim, setAnim]         = useState(null);
  const [modal, setModal]       = useState(null);
  const startPos = useRef(null);

  // Auth
  const [user, setUser]               = useState(null);
  const [showLogin, setShowLogin]     = useState(false);
  const [showSidebar, setShowSidebar] = useState(false);
  const [phoneInput, setPhoneInput]   = useState("");
  const [authLoading, setAuthLoading] = useState(false);
  const [authMsg, setAuthMsg]         = useState("");
  const [newAccount, setNewAccount]   = useState(null);

  // Sidebar
  const [myNeeds, setMyNeeds]         = useState([]);
  const [myHelp, setMyHelp]           = useState([]);
  const [sideTab, setSideTab]         = useState("needs");
  const [helpersModal, setHelpersModal] = useState(null);
  const [helpers, setHelpers]         = useState([]);
  const [helpersLoading, setHelpersLoading] = useState(false);

  // Floating save button
  const [showSaveBtn, setShowSaveBtn] = useState(false);
  const swipeCountRef = useRef(0);
  const saveBtnTimer  = useRef(null);

  // Guest skip/help tracking
  const [guestSkipped, setGuestSkipped] = useState(() => {
    try { return JSON.parse(localStorage.getItem(LS_SKIP) || "[]"); } catch { return []; }
  });
  const [guestHelped, setGuestHelped] = useState(() => {
    try { return JSON.parse(localStorage.getItem(LS_HELP) || "[]"); } catch { return []; }
  });

  // Post form
  const [fCat, setFCat]         = useState(CATEGORIES[0].name);
  const [fTitle, setFTitle]     = useState("");
  const [fDesc, setFDesc]       = useState("");
  const [fLoc, setFLoc]         = useState("");
  const [fUrg, setFUrg]         = useState(URGENCIES[0]);
  const [fName, setFName]       = useState("");
  const [fPhone, setFPhone]     = useState("");
  const [formErr, setFormErr]   = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess]   = useState(false);

  // Load user + needs on mount
  useEffect(() => {
    const saved = localStorage.getItem(LS_KEY);
    if (saved) {
      const u = JSON.parse(saved);
      setUser(u);
      if (u.phone) setFPhone(u.phone);
      loadNeeds(u.account_id);
    } else {
      loadNeeds(null);
    }
  }, []);

  const loadNeeds = (account_id) => {
    const url = account_id ? `${API_URL}/needs.php?account_id=${account_id}` : `${API_URL}/needs.php`;
    fetch(url)
      .then(r => r.json())
      .then(j => {
        if (j.data) {
          let needs = j.data.map(n => ({ ...(CATEGORIES.find(c => c.name === n.category) || CATEGORIES[5]), ...n }));
          // Filter guest skipped/helped from seed
          if (!account_id) {
            const skipped = JSON.parse(localStorage.getItem(LS_SKIP) || "[]");
            const helped  = JSON.parse(localStorage.getItem(LS_HELP) || "[]");
            needs = needs.filter(n => !skipped.includes(String(n.id)) && !helped.includes(String(n.id)));
          }
          setDbNeeds(needs);
        }
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  // Load sidebar data
  useEffect(() => {
    if (!user) return;
    fetch(`${API_URL}/my-needs.php?account_id=${user.account_id}`)
      .then(r => r.json()).then(j => { if (j.data) setMyNeeds(j.data); }).catch(() => {});
    fetch(`${API_URL}/my-help.php?account_id=${user.account_id}`)
      .then(r => r.json()).then(j => { if (j.data) setMyHelp(j.data); }).catch(() => {});
  }, [user]);

  const needs   = [...dbNeeds, ...SEED.filter(s => !guestSkipped.includes(s.id) && !guestHelped.includes(s.id))];
  const card    = needs[idx];
  const next    = needs[idx + 1];
  const rotation  = offset.x * 0.07;
  const overlayOp = Math.min(Math.abs(offset.x) / 130, 1);
  const showR = offset.x > 25;
  const showL = offset.x < -25;

  const triggerSaveBtn = () => {
    if (user) return;
    swipeCountRef.current += 1;
    if (swipeCountRef.current % 3 === 0) {
      setShowSaveBtn(true);
      clearTimeout(saveBtnTimer.current);
      saveBtnTimer.current = setTimeout(() => setShowSaveBtn(false), 5000);
    }
  };

  const trackHelp = (need) => {
    if (!need) return;
    const id = String(need.id);
    if (user && !id.startsWith("s")) {
      fetch(`${API_URL}/track-help.php`, { method:"POST", headers:{"Content-Type":"application/json"}, body:JSON.stringify({ account_id:user.account_id, need_id:need.id }) }).catch(() => {});
      setMyHelp(prev => prev.find(n => String(n.id) === id) ? prev : [need, ...prev]);
    } else if (!user) {
      const updated = [...guestHelped, id];
      setGuestHelped(updated);
      localStorage.setItem(LS_HELP, JSON.stringify(updated));
    }
  };

  const trackSkip = (need) => {
    if (!need) return;
    const id = String(need.id);
    if (user && !id.startsWith("s")) {
      fetch(`${API_URL}/track-skip.php`, { method:"POST", headers:{"Content-Type":"application/json"}, body:JSON.stringify({ account_id:user.account_id, need_id:need.id }) }).catch(() => {});
    } else if (!user) {
      const updated = [...guestSkipped, id];
      setGuestSkipped(updated);
      localStorage.setItem(LS_SKIP, JSON.stringify(updated));
    }
  };

  const onStart = e => { const p=e.touches?e.touches[0]:e; startPos.current={x:p.clientX,y:p.clientY}; setDragging(true); };
  const onMove  = e => { if(!dragging||!startPos.current) return; const p=e.touches?e.touches[0]:e; setOffset({x:p.clientX-startPos.current.x,y:p.clientY-startPos.current.y}); };
  const onEnd   = () => { if(!dragging) return; setDragging(false); if(offset.x>100) doRight(); else if(offset.x<-100) doLeft(); setOffset({x:0,y:0}); startPos.current=null; };

  const doRight = () => {
    const current = needs[idx];
    setAnim("right");
    setTimeout(() => {
      setModal(current);
      trackHelp(current);
      setAnim(null);
      setIdx(i => i+1);
      triggerSaveBtn();
    }, 300);
  };

  const doLeft = () => {
    const current = needs[idx];
    setAnim("left");
    setTimeout(() => {
      trackSkip(current);
      setAnim(null);
      setIdx(i => i+1);
      triggerSaveBtn();
    }, 300);
  };

  const doAuth = async () => {
    if (!phoneInput.trim()) { setAuthMsg("أدخل رقم هاتفك"); return; }
    setAuthLoading(true); setAuthMsg("");
    try {
      const res  = await fetch(`${API_URL}/auth.php`, { method:"POST", headers:{"Content-Type":"application/json"}, body:JSON.stringify({ action:"login", phone:phoneInput.trim() }) });
      const json = await res.json();
      if (json.success) {
        const u = { account_id:json.account_id, phone:json.phone, created_at:json.created_at };
        localStorage.setItem(LS_KEY, JSON.stringify(u));
        setUser(u); setFPhone(u.phone);
        if (json.type === "register") setNewAccount(json.account_id);
        else { setShowLogin(false); setShowSaveBtn(false); loadNeeds(u.account_id); }
      } else { setAuthMsg(json.error || "حدث خطأ"); }
    } catch { setAuthMsg("تعذّر الاتصال بالخادم"); }
    finally { setAuthLoading(false); }
  };

  const doLogout = () => {
    localStorage.removeItem(LS_KEY);
    setUser(null); setMyNeeds([]); setMyHelp([]);
    setShowSidebar(false); setNewAccount(null);
    loadNeeds(null);
  };

  const loadHelpers = async (need) => {
    if (!user) return;
    setHelpersModal(need);
    setHelpers([]);
    setHelpersLoading(true);
    try {
      const res  = await fetch(`${API_URL}/need-helpers.php?need_id=${need.id}&account_id=${user.account_id}`);
      const json = await res.json();
      if (json.data) setHelpers(json.data);
    } catch {} finally { setHelpersLoading(false); }
  };

  const submitNeed = async () => {
    if (!fTitle.trim()||!fDesc.trim()||!fLoc.trim()||!fName.trim()||!fPhone.trim()) { setFormErr("الرجاء تعبئة جميع الحقول."); return; }
    setFormErr(""); setSubmitting(true);
    const cat = CATEGORIES.find(c => c.name === fCat);
    const payload = { category:cat.name, emoji:cat.emoji, color:cat.color, bg:cat.bg, title:fTitle.trim(), description:fDesc.trim(), location:fLoc.trim(), urgency:fUrg, name:fName.trim(), phone:fPhone.trim(), ...(user?{account_id:user.account_id}:{}) };
    try {
      const res  = await fetch(`${API_URL}/submit.php`, { method:"POST", headers:{"Content-Type":"application/json"}, body:JSON.stringify(payload) });
      const json = await res.json();
      if (json.success) {
        const newNeed = { ...cat, ...payload, id:json.id, created_at:new Date().toISOString() };
        setDbNeeds(prev => [newNeed, ...prev]);
        if (user) setMyNeeds(prev => [newNeed, ...prev]);
        setSuccess(true);
        setFTitle(""); setFDesc(""); setFLoc(""); setFName("");
        setFCat(CATEGORIES[0].name); setFUrg(URGENCIES[0]); setIdx(0);
      } else { setFormErr("حدث خطأ، يرجى المحاولة مجدداً."); }
    } catch { setFormErr("تعذّر الاتصال بالخادم."); }
    finally { setSubmitting(false); }
  };

  const inp = { width:"100%", background:SURFACE2, border:`1.5px solid ${BORDER}`, borderRadius:12, padding:"12px 15px", color:TEXT_PRI, fontSize:14.5, fontFamily:FONT, outline:"none", boxSizing:"border-box" };
  const lbl = { color:TEXT_SEC, fontSize:13, fontWeight:700, marginBottom:6, display:"block", fontFamily:FONT };

  const Sheet = ({ onClose, children }) => (
    <div onClick={onClose} style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.4)", display:"flex", alignItems:"flex-end", zIndex:120, backdropFilter:"blur(4px)" }}>
      <div dir="rtl" onClick={e=>e.stopPropagation()} style={{ width:"100%", maxWidth:430, margin:"0 auto", background:SURFACE, borderRadius:"22px 22px 0 0", padding:"20px 20px 28px", maxHeight:"88dvh", overflowY:"auto", border:`1px solid ${BORDER}`, borderBottom:"none", animation:"su 0.28s ease", fontFamily:FONT }}>
        <div style={{ width:36, height:4, background:SURFACE2, borderRadius:2, margin:"0 auto 18px" }} />
        {children}
      </div>
    </div>
  );

  return (
    <div dir="rtl" style={{ height:"100dvh", maxHeight:"100dvh", background:BG, display:"flex", flexDirection:"column", alignItems:"center", fontFamily:FONT, userSelect:"none", position:"relative", overflow:"hidden" }}>
      <div style={{ position:"relative", zIndex:1, width:"100%", maxWidth:430, height:"100%", display:"flex", flexDirection:"column" }}>

        {/* Header */}
        <div style={{ padding:"12px 20px 4px", display:"flex", justifyContent:"space-between", alignItems:"center", flexShrink:0, background:SURFACE, borderBottom:`1px solid ${BORDER}` }}>
          {user ? (
            <button onClick={() => setShowSidebar(true)} style={{ width:36, height:36, borderRadius:10, background:SURFACE2, border:`1px solid ${GREEN}40`, color:GREEN_DARK, fontSize:17, cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"center" }}>☰</button>
          ) : (
            <button onClick={() => setShowLogin(true)} style={{ width:36, height:36, borderRadius:10, background:SURFACE2, border:`1px solid ${BORDER}`, color:TEXT_SEC, fontSize:13, cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"center", fontFamily:FONT, fontWeight:700 }}>دخول</button>
          )}
          <div style={{ display:"flex", alignItems:"center", gap:8 }}>
            <span style={{ fontSize:22 }}>🤲</span>
            <div>
              <div style={{ fontWeight:900, fontSize:22, color:TEXT_PRI, letterSpacing:-0.5, fontFamily:FONT }}>صدقة</div>
              <div style={{ color:TEXT_MUT, fontSize:9.5, fontWeight:700, letterSpacing:3, marginTop:-2, fontFamily:FONT }}>SADAQA.LY</div>
            </div>
          </div>
          {user ? (
            <div style={{ background:`${GREEN}15`, border:`1px solid ${GREEN}30`, borderRadius:10, padding:"4px 10px", color:GREEN_DARK, fontSize:11, fontWeight:700, fontFamily:FONT }}>{user.account_id}</div>
          ) : <div style={{ width:36 }} />}
        </div>

        {/* FEED */}
        {view === "feed" && (
          <div style={{ flex:1, minHeight:0, display:"flex", flexDirection:"column" }}>
            {loading ? (
              <div style={{ flex:1, display:"flex", alignItems:"center", justifyContent:"center", color:TEXT_MUT, fontSize:15, fontFamily:FONT }}>جارٍ تحميل الاحتياجات…</div>
            ) : idx >= needs.length ? (
              /* End of feed popup */
              <div style={{ flex:1, position:"relative", display:"flex", flexDirection:"column" }}>
                <div style={{ position:"absolute", inset:0, background:"rgba(245,247,245,0.85)", backdropFilter:"blur(6px)", display:"flex", alignItems:"center", justifyContent:"center", padding:24 }}>
                  <div style={{ background:SURFACE, borderRadius:20, padding:"28px 24px", width:"100%", textAlign:"center", boxShadow:"0 8px 40px rgba(0,0,0,0.12)", border:`1px solid ${BORDER}` }}>
                    <div style={{ fontSize:52, marginBottom:14 }}>🤲</div>
                    <h2 style={{ color:TEXT_PRI, fontSize:22, fontWeight:900, marginBottom:8, fontFamily:FONT }}>محتاج حاجة؟</h2>
                    <p style={{ color:TEXT_SEC, fontSize:14, lineHeight:1.75, marginBottom:22, fontFamily:FONT }}>انشر احتياجك وسيصل لآلاف المتطوعين في ليبيا</p>
                    <button onClick={() => { setView("post"); setSuccess(false); }} style={{ width:"100%", background:`linear-gradient(135deg,${GREEN},${GREEN_DARK})`, border:"none", borderRadius:30, padding:"14px", color:"#fff", fontSize:16, fontWeight:800, cursor:"pointer", fontFamily:FONT, marginBottom:14, boxShadow:`0 4px 16px ${GREEN}40` }}>انشر احتياجك الآن 🚀</button>
                    <p style={{ color:TEXT_MUT, fontSize:12, fontFamily:FONT }}>سيتم إعلامك عند وجود احتياجات جديدة 🔔</p>
                  </div>
                </div>
              </div>
            ) : (
              <>
                <div style={{ color:TEXT_MUT, fontSize:12.5, textAlign:"center", padding:"6px 0 8px", fontWeight:600, flexShrink:0, fontFamily:FONT }}>{idx+1} / {needs.length}</div>
                <div style={{ flex:1, minHeight:0, position:"relative", margin:"0 14px" }}>
                  {next && (
                    <div style={{ position:"absolute", inset:0, transform:"scale(0.94) translateY(10px)", borderRadius:20, overflow:"hidden", zIndex:1, opacity:0.5 }}>
                      <CardContent card={next} />
                    </div>
                  )}
                  <div
                    onMouseDown={onStart} onMouseMove={onMove} onMouseUp={onEnd} onMouseLeave={onEnd}
                    onTouchStart={onStart} onTouchMove={onMove} onTouchEnd={onEnd}
                    style={{ position:"absolute", inset:0, transform:anim==="right"?"translateX(160%) rotate(22deg)":anim==="left"?"translateX(-160%) rotate(-22deg)":`translateX(${offset.x}px) translateY(${offset.y*0.15}px) rotate(${rotation}deg)`, transition:dragging?"none":"transform 0.3s cubic-bezier(.25,.46,.45,.94)", cursor:dragging?"grabbing":"grab", zIndex:2, borderRadius:20, overflow:"hidden", boxShadow:"0 8px 40px rgba(0,0,0,0.12)" }}
                  >
                    {showR && <div style={{ position:"absolute", top:20, right:14, border:`2.5px solid ${DANGER}`, borderRadius:8, padding:"4px 14px", color:DANGER, fontSize:20, fontWeight:900, opacity:overlayOp, zIndex:10, transform:"rotate(10deg)", fontFamily:FONT, background:"rgba(255,255,255,0.9)" }}>تخطّي</div>}
                    {showL && <div style={{ position:"absolute", top:20, left:14, border:`2.5px solid ${GREEN}`, borderRadius:8, padding:"4px 14px", color:GREEN_DARK, fontSize:20, fontWeight:900, opacity:overlayOp, zIndex:10, transform:"rotate(-10deg)", fontFamily:FONT, background:"rgba(255,255,255,0.9)" }}>مساعدة!</div>}
                    <CardContent card={card} />
                  </div>
                </div>
                <div style={{ display:"flex", justifyContent:"center", alignItems:"center", gap:32, padding:"14px 20px 10px", flexShrink:0 }}>
                  <button onClick={doRight} style={{ width:52, height:52, borderRadius:"50%", border:`2px solid ${GREEN}50`, background:`${GREEN}10`, fontSize:22, cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"center", color:GREEN_DARK, boxShadow:`0 2px 12px ${GREEN}20` }}>♥</button>
                  <div style={{ color:TEXT_MUT, fontSize:12, textAlign:"center", lineHeight:1.5, fontWeight:600, fontFamily:FONT }}>اسحب أو<br/>اضغط</div>
                  <button onClick={doLeft} style={{ width:52, height:52, borderRadius:"50%", border:`2px solid ${DANGER}50`, background:`${DANGER}08`, fontSize:22, cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"center", color:DANGER, boxShadow:`0 2px 12px ${DANGER}15` }}>✕</button>
                </div>
              </>
            )}
          </div>
        )}

        {/* POST */}
        {view === "post" && (
          <div style={{ flex:1, minHeight:0, overflowY:"auto", padding:"6px 18px 80px" }}>
            {success ? (
              <div style={{ textAlign:"center", color:TEXT_PRI, padding:"36px 20px" }}>
                <div style={{ fontSize:52, marginBottom:12 }}>✅</div>
                <h2 style={{ fontSize:22, fontWeight:800, marginBottom:10, fontFamily:FONT }}>تم نشر احتياجك!</h2>
                <p style={{ color:TEXT_SEC, fontSize:14.5, lineHeight:1.75, marginBottom:24, fontFamily:FONT }}>أصبح طلبك مرئياً للجميع. 🤝</p>
                <button onClick={() => { setSuccess(false); setView("feed"); }} style={{ background:`linear-gradient(135deg,${GREEN},${GREEN_DARK})`, border:"none", borderRadius:30, padding:"13px 34px", color:"#fff", fontSize:16, fontWeight:800, cursor:"pointer", fontFamily:FONT }}>تصفّح الاحتياجات</button>
              </div>
            ) : (
              <>
                <h2 style={{ color:TEXT_PRI, fontSize:20, fontWeight:800, marginBottom:2, textAlign:"center", fontFamily:FONT }}>انشر احتياجك 📢</h2>
                <p style={{ color:TEXT_MUT, fontSize:13, textAlign:"center", marginBottom:18, fontFamily:FONT }}>شارك طلبك وسيصل لمن يستطيع المساعدة</p>
                <label style={lbl}>نوع المساعدة</label>
                <div style={{ display:"flex", flexWrap:"wrap", gap:8, marginBottom:18 }}>
                  {CATEGORIES.map(c => <button key={c.name} onClick={() => setFCat(c.name)} style={{ flex:"1 0 30%", background:fCat===c.name?c.bg:SURFACE2, border:`1.5px solid ${fCat===c.name?c.color+"60":BORDER}`, borderRadius:12, padding:"10px 6px", color:fCat===c.name?c.color:TEXT_SEC, fontSize:12.5, fontWeight:700, cursor:"pointer", fontFamily:FONT }}>{c.emoji} {c.name}</button>)}
                </div>
                <label style={lbl}>عنوان الطلب</label>
                <input style={{ ...inp, marginBottom:14 }} value={fTitle} onChange={e=>setFTitle(e.target.value)} placeholder="مثال: عاجل — مطلوب متبرع بالدم" />
                <label style={lbl}>تفاصيل الطلب</label>
                <textarea style={{ ...inp, marginBottom:14, minHeight:80, resize:"vertical" }} value={fDesc} onChange={e=>setFDesc(e.target.value)} placeholder="اشرح احتياجك بالتفصيل…" />
                <label style={lbl}>الموقع</label>
                <input style={{ ...inp, marginBottom:14 }} value={fLoc} onChange={e=>setFLoc(e.target.value)} placeholder="مثال: طرابلس، ليبيا" />
                <label style={lbl}>درجة الأهمية</label>
                <div style={{ display:"flex", flexWrap:"wrap", gap:8, marginBottom:18 }}>
                  {URGENCIES.map(u => <button key={u} onClick={() => setFUrg(u)} style={{ flex:"1 0 45%", background:fUrg===u?`${GREEN}15`:SURFACE2, border:`1.5px solid ${fUrg===u?GREEN+"60":BORDER}`, borderRadius:12, padding:"9px 6px", color:fUrg===u?GREEN_DARK:TEXT_SEC, fontSize:12.5, fontWeight:600, cursor:"pointer", fontFamily:FONT }}>{u}</button>)}
                </div>
                <label style={lbl}>الاسم</label>
                <input style={{ ...inp, marginBottom:14 }} value={fName} onChange={e=>setFName(e.target.value)} placeholder="اسمك الكامل" />
                <label style={lbl}>رقم الهاتف</label>
                <input style={{ ...inp, marginBottom:18, direction:"ltr", textAlign:"right" }} value={fPhone} onChange={e=>setFPhone(e.target.value)} placeholder="+218 9X-XXX-XXXX" />
                {formErr && <div style={{ background:`${DANGER}10`, color:DANGER, border:`1px solid ${DANGER}25`, borderRadius:10, padding:"10px 14px", fontSize:13.5, marginBottom:14, textAlign:"center", fontFamily:FONT }}>{formErr}</div>}
                <button onClick={submitNeed} disabled={submitting} style={{ width:"100%", background:submitting?SURFACE2:`linear-gradient(135deg,${GREEN},${GREEN_DARK})`, border:"none", borderRadius:30, padding:"14px", color:submitting?TEXT_MUT:"#fff", fontSize:16, fontWeight:800, cursor:submitting?"not-allowed":"pointer", fontFamily:FONT, boxShadow:submitting?"none":`0 4px 16px ${GREEN}40` }}>{submitting?"جارٍ النشر…":"نشر الاحتياج 🚀"}</button>
              </>
            )}
          </div>
        )}

        {/* NAV — only تصفّح */}
        <div style={{ flexShrink:0, background:SURFACE, borderTop:`1px solid ${BORDER}`, display:"flex", padding:"8px 0 10px", position:"relative" }}>
          <button onClick={() => setView("feed")} style={{ flex:1, background:"transparent", border:"none", cursor:"pointer", display:"flex", flexDirection:"column", alignItems:"center", gap:3, color:view==="feed"?GREEN_DARK:TEXT_MUT, fontFamily:FONT }}>
            <span style={{ fontSize:20 }}>🃏</span>
            <span style={{ fontSize:11.5, fontWeight:700 }}>تصفّح</span>
            {view==="feed" && <div style={{ width:20, height:2.5, background:GREEN, borderRadius:2, marginTop:1 }} />}
          </button>

          {/* Floating ➕ button */}
          <button onClick={() => { setView(view==="post"?"feed":"post"); setSuccess(false); }} style={{ position:"absolute", bottom:12, left:"50%", transform:`translateX(-50%) ${view==="post"?"rotate(45deg)":"rotate(0deg)"}`, width:40, height:40, borderRadius:"50%", background:view==="post"?`linear-gradient(135deg,${DANGER},#ff6b6b)`:`linear-gradient(135deg,${GREEN},${GREEN_DARK})`, border:"none", fontSize:22, color:"#fff", cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"center", boxShadow:view==="post"?`0 4px 16px ${DANGER}40`:`0 4px 16px ${GREEN}40`, transition:"all 0.3s ease", zIndex:10 }}>+</button>
        </div>
      </div>

      {/* Floating Save Button */}
      {showSaveBtn && !user && (
        <div onClick={() => { setShowLogin(true); setShowSaveBtn(false); }} style={{ position:"fixed", bottom:90, left:"50%", transform:"translateX(-50%)", background:`linear-gradient(135deg,${GREEN},${GREEN_DARK})`, border:"none", borderRadius:30, padding:"12px 22px", color:"#fff", fontSize:14, fontWeight:800, cursor:"pointer", fontFamily:FONT, zIndex:50, boxShadow:`0 8px 30px ${GREEN}50`, whiteSpace:"nowrap", animation:"fadeInUp 0.3s ease", display:"flex", alignItems:"center", gap:8 }}>
          <span>📖</span> قم بحفظ سجل كل الصدقات
          <button onClick={e => { e.stopPropagation(); setShowSaveBtn(false); }} style={{ background:"rgba(255,255,255,0.25)", border:"none", borderRadius:"50%", width:20, height:20, color:"#fff", fontSize:12, cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"center" }}>✕</button>
        </div>
      )}

      {/* Login Popup */}
      {showLogin && !user && (
        <div onClick={() => { setShowLogin(false); setAuthMsg(""); }} style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.4)", display:"flex", alignItems:"center", justifyContent:"center", zIndex:100, backdropFilter:"blur(6px)", padding:20 }}>
          <div dir="rtl" onClick={e=>e.stopPropagation()} style={{ background:SURFACE, borderRadius:20, padding:"28px 24px", width:"100%", maxWidth:360, border:`1px solid ${BORDER}`, boxShadow:"0 20px 60px rgba(0,0,0,0.15)", position:"relative", fontFamily:FONT }}>
            <button onClick={() => { setShowLogin(false); setAuthMsg(""); }} style={{ position:"absolute", top:14, left:14, background:"transparent", border:"none", color:TEXT_MUT, fontSize:18, cursor:"pointer" }}>✕</button>
            {newAccount ? (
              <div style={{ textAlign:"center" }}>
                <div style={{ fontSize:44, marginBottom:12 }}>🎉</div>
                <h2 style={{ color:TEXT_PRI, fontSize:19, fontWeight:800, marginBottom:8, fontFamily:FONT }}>تم إنشاء حسابك!</h2>
                <p style={{ color:TEXT_SEC, fontSize:13, marginBottom:16, lineHeight:1.7, fontFamily:FONT }}>احفظ رقم حسابك للدخول لاحقاً</p>
                <div style={{ background:`${GREEN}10`, border:`2px dashed ${GREEN}50`, borderRadius:14, padding:"16px", marginBottom:20 }}>
                  <div style={{ color:TEXT_MUT, fontSize:12, marginBottom:4, fontFamily:FONT }}>رقم حسابك</div>
                  <div style={{ color:GREEN_DARK, fontSize:28, fontWeight:900, letterSpacing:3, fontFamily:FONT }}>{newAccount}</div>
                </div>
                <button onClick={() => { setShowLogin(false); setNewAccount(null); setShowSaveBtn(false); loadNeeds(user?.account_id); }} style={{ width:"100%", background:`linear-gradient(135deg,${GREEN},${GREEN_DARK})`, border:"none", borderRadius:30, padding:"13px", color:"#fff", fontSize:15, fontWeight:800, cursor:"pointer", fontFamily:FONT }}>ابدأ الآن 🚀</button>
              </div>
            ) : (
              <>
                <div style={{ textAlign:"center", marginBottom:20 }}>
                  <div style={{ fontSize:36, marginBottom:8 }}>📖</div>
                  <h2 style={{ color:TEXT_PRI, fontSize:18, fontWeight:800, marginBottom:6, fontFamily:FONT }}>سجّل صدقاتك</h2>
                  <p style={{ color:TEXT_SEC, fontSize:13, lineHeight:1.7, fontFamily:FONT }}>أدخل رقم هاتفك لحفظ سجل مساعداتك واحتياجاتك</p>
                </div>
                <label style={lbl}>رقم الهاتف</label>
                <input style={{ ...inp, marginBottom:14, direction:"ltr", textAlign:"right" }} value={phoneInput} onChange={e=>setPhoneInput(e.target.value)} onKeyDown={e=>e.key==="Enter"&&doAuth()} placeholder="+218 9X-XXX-XXXX" />
                {authMsg && <div style={{ color:DANGER, fontSize:13, marginBottom:12, textAlign:"center", fontFamily:FONT }}>{authMsg}</div>}
                <button onClick={doAuth} disabled={authLoading} style={{ width:"100%", background:authLoading?SURFACE2:`linear-gradient(135deg,${GREEN},${GREEN_DARK})`, border:"none", borderRadius:30, padding:"13px", color:authLoading?TEXT_MUT:"#fff", fontSize:15, fontWeight:800, cursor:authLoading?"not-allowed":"pointer", fontFamily:FONT, marginBottom:10 }}>{authLoading?"جارٍ التحقق…":"دخول / تسجيل"}</button>
                <p style={{ color:TEXT_MUT, fontSize:12, textAlign:"center", lineHeight:1.7, fontFamily:FONT }}>إذا كنت مسجّلاً مسبقاً سيتم تسجيل دخولك تلقائياً</p>
              </>
            )}
          </div>
        </div>
      )}

      {/* Sidebar */}
      {showSidebar && user && (
        <div onClick={() => setShowSidebar(false)} style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.3)", zIndex:110, backdropFilter:"blur(4px)" }}>
          <div dir="rtl" onClick={e=>e.stopPropagation()} style={{ position:"absolute", top:0, right:0, bottom:0, width:"85%", maxWidth:320, background:SURFACE, display:"flex", flexDirection:"column", animation:"slideIn 0.28s ease", boxShadow:"-8px 0 40px rgba(0,0,0,0.15)", fontFamily:FONT }}>

            {/* Profile */}
            <div style={{ padding:"24px 20px 16px", borderBottom:`1px solid ${BORDER}`, flexShrink:0, background:SURFACE }}>
              <div style={{ display:"flex", justifyContent:"flex-end", marginBottom:16 }}>
                <button onClick={() => setShowSidebar(false)} style={{ background:"transparent", border:"none", color:TEXT_MUT, fontSize:20, cursor:"pointer" }}>✕</button>
              </div>
              <div style={{ display:"flex", alignItems:"center", gap:12 }}>
                <div style={{ width:48, height:48, borderRadius:"50%", background:`${GREEN}15`, border:`2px solid ${GREEN}30`, display:"flex", alignItems:"center", justifyContent:"center", fontSize:22 }}>🤲</div>
                <div>
                  <div style={{ color:TEXT_PRI, fontWeight:800, fontSize:16, fontFamily:FONT }}>{user.account_id}</div>
                  <div style={{ color:TEXT_SEC, fontSize:13, direction:"ltr", fontFamily:FONT }}>{user.phone}</div>
                  <div style={{ color:TEXT_MUT, fontSize:11, marginTop:2, fontFamily:FONT }}>انضممت {timeAgo(user.created_at)}</div>
                </div>
              </div>
            </div>

            {/* Tabs */}
            <div style={{ display:"flex", borderBottom:`1px solid ${BORDER}`, flexShrink:0 }}>
              {[{id:"needs",label:`احتياجاتي (${myNeeds.length})`},{id:"help",label:`مساعداتي (${myHelp.length})`}].map(t => (
                <button key={t.id} onClick={() => setSideTab(t.id)} style={{ flex:1, background:"transparent", border:"none", padding:"12px 8px", color:sideTab===t.id?GREEN_DARK:TEXT_MUT, fontSize:13, fontWeight:700, cursor:"pointer", fontFamily:FONT, borderBottom:sideTab===t.id?`2px solid ${GREEN}`:"2px solid transparent" }}>{t.label}</button>
              ))}
            </div>

            {/* List */}
            <div style={{ flex:1, overflowY:"auto", padding:"12px 16px" }}>
              {(sideTab==="needs" ? myNeeds : myHelp).length === 0 ? (
                <div style={{ textAlign:"center", color:TEXT_MUT, padding:"40px 20px", fontSize:14, fontFamily:FONT }}>
                  {sideTab==="needs" ? "لم تنشر أي احتياجات بعد" : "لم تساعد في أي طلب بعد"}
                </div>
              ) : (
                (sideTab==="needs" ? myNeeds : myHelp).map(n => (
                  <div key={n.id} onClick={() => sideTab==="needs" && loadHelpers(n)} style={{ background:SURFACE2, borderRadius:12, padding:"12px 14px", marginBottom:10, border:`1px solid ${BORDER}`, cursor:sideTab==="needs"?"pointer":"default", position:"relative" }}>
                    <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:6 }}>
                      <span style={{ fontSize:18 }}>{n.emoji}</span>
                      <span style={{ color:n.color, fontSize:11.5, fontWeight:700, fontFamily:FONT }}>{n.category}</span>
                      {sideTab==="needs" && (
                        <span style={{ background:`${GREEN}15`, color:GREEN_DARK, fontSize:10, fontWeight:700, padding:"2px 8px", borderRadius:10, marginRight:"auto", fontFamily:FONT }}>اضغط للتفاصيل</span>
                      )}
                    </div>
                    <div style={{ color:TEXT_PRI, fontSize:13.5, fontWeight:700, marginBottom:4, fontFamily:FONT }}>{n.title}</div>
                    <div style={{ color:TEXT_MUT, fontSize:12, fontFamily:FONT }}>📍 {n.location} · {timeAgo(n.created_at)}</div>
                    {sideTab==="needs" && <div style={{ position:"absolute", left:14, top:"50%", transform:"translateY(-50%)", color:TEXT_MUT, fontSize:16 }}>‹</div>}
                  </div>
                ))
              )}
            </div>

            {/* Logout */}
            <div style={{ padding:"14px 16px", borderTop:`1px solid ${BORDER}`, flexShrink:0 }}>
              <button onClick={doLogout} style={{ width:"100%", background:`${DANGER}08`, border:`1px solid ${DANGER}25`, borderRadius:12, padding:"12px", color:DANGER, fontSize:14, fontWeight:700, cursor:"pointer", fontFamily:FONT }}>تسجيل الخروج 🚪</button>
            </div>
          </div>
        </div>
      )}

      {/* Helpers Sheet */}
      {helpersModal && (
        <Sheet onClose={() => { setHelpersModal(null); setHelpers([]); }}>
          <div style={{ display:"flex", alignItems:"center", gap:10, marginBottom:16, paddingBottom:14, borderBottom:`1px solid ${BORDER}` }}>
            <span style={{ fontSize:28 }}>{helpersModal.emoji}</span>
            <div>
              <div style={{ color:TEXT_PRI, fontSize:15, fontWeight:800, fontFamily:FONT }}>{helpersModal.title}</div>
              <div style={{ color:TEXT_MUT, fontSize:12, fontFamily:FONT }}>{helpersLoading ? "جارٍ التحميل…" : `${helpers.length} شخص يريد المساعدة`}</div>
            </div>
          </div>
          {helpersLoading ? (
            <div style={{ textAlign:"center", color:TEXT_MUT, padding:"30px", fontFamily:FONT }}>جارٍ التحميل…</div>
          ) : helpers.length === 0 ? (
            <div style={{ textAlign:"center", padding:"24px 16px" }}>
              <div style={{ fontSize:40, marginBottom:12 }}>🕐</div>
              <p style={{ color:TEXT_PRI, fontSize:15, fontWeight:800, marginBottom:8, fontFamily:FONT }}>لا أحد بعد</p>
              <p style={{ color:TEXT_SEC, fontSize:13, lineHeight:1.7, marginBottom:18, fontFamily:FONT }}>شارك طلبك مع أصدقائك ليصل لأكبر عدد من الناس</p>
              <a href={`https://wa.me/?text=${encodeURIComponent("محتاج مساعدة: " + helpersModal.title + " — " + helpersModal.location)}`} target="_blank" rel="noopener noreferrer" style={{ display:"inline-flex", alignItems:"center", gap:8, background:"#25D36615", border:"1px solid #25D36640", borderRadius:20, padding:"10px 20px", color:"#25D366", fontSize:14, fontWeight:800, textDecoration:"none", fontFamily:FONT }}>
                شارك عبر واتساب 💚
              </a>
            </div>
          ) : (
            helpers.map((h, i) => (
              <div key={i} style={{ background:SURFACE2, borderRadius:14, padding:"14px 16px", marginBottom:10, border:`1px solid ${BORDER}` }}>
                <div style={{ display:"flex", alignItems:"center", gap:10, marginBottom:10 }}>
                  <div style={{ width:38, height:38, borderRadius:"50%", background:`${GREEN}15`, border:`1.5px solid ${GREEN}30`, display:"flex", alignItems:"center", justifyContent:"center", fontSize:16 }}>👤</div>
                  <div>
                    <div style={{ color:TEXT_PRI, fontSize:13, fontWeight:700, fontFamily:FONT }}>{h.account_id}</div>
                    <div style={{ color:GREEN_DARK, fontSize:12, direction:"ltr", fontFamily:FONT }}>{h.phone}</div>
                  </div>
                </div>
                <a href={`https://wa.me/${h.phone.replace(/[^0-9]/g,"")}?text=${encodeURIComponent("السلام عليكم، أنا صاحب الطلب: " + helpersModal.title + " على تطبيق صدقة")}`} target="_blank" rel="noopener noreferrer" style={{ display:"flex", alignItems:"center", justifyContent:"center", gap:6, background:"#25D36615", border:"1px solid #25D36640", borderRadius:20, padding:"8px 14px", color:"#25D366", fontSize:13, fontWeight:800, textDecoration:"none", fontFamily:FONT }}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="#25D366"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.872.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
                  تواصل عبر واتساب
                </a>
              </div>
            ))
          )}
        </Sheet>
      )}

      {/* Contact Sheet */}
      {modal && (
        <Sheet onClose={() => setModal(null)}>
          <div style={{ textAlign:"center", marginBottom:20 }}>
            <div style={{ fontSize:44, marginBottom:10 }}>{modal.emoji}</div>
            <h2 style={{ color:TEXT_PRI, fontSize:19, fontWeight:800, marginBottom:8, fontFamily:FONT }}>{modal.title}</h2>
            <Badge color={modal.color} bg={modal.bg}>{modal.category}</Badge>
          </div>
          <div style={{ background:SURFACE2, borderRadius:14, padding:"14px 16px", marginBottom:14, border:`1px solid ${BORDER}` }}>
            <p style={{ color:TEXT_SEC, lineHeight:1.75, margin:0, fontSize:14, fontFamily:FONT }}>{modal.description}</p>
          </div>
          <div style={{ display:"flex", gap:10, marginBottom:14 }}>
            {[{icon:"👤",label:"الاسم",val:modal.name,col:TEXT_PRI},{icon:"📞",label:"الهاتف",val:modal.phone,col:GREEN_DARK}].map(r => (
              <div key={r.label} style={{ flex:1, background:SURFACE2, borderRadius:14, padding:"14px 10px", textAlign:"center", border:`1px solid ${BORDER}` }}>
                <div style={{ fontSize:20, marginBottom:5 }}>{r.icon}</div>
                <div style={{ color:TEXT_MUT, fontSize:11, marginBottom:4, fontFamily:FONT }}>{r.label}</div>
                <div style={{ color:r.col, fontWeight:700, fontSize:14, direction:"ltr", fontFamily:FONT }}>{r.val}</div>
              </div>
            ))}
          </div>
          <div style={{ background:SURFACE2, borderRadius:12, padding:"10px 16px", marginBottom:14, display:"flex", justifyContent:"space-between", border:`1px solid ${BORDER}` }}>
            <span style={{ color:TEXT_SEC, fontSize:13, fontFamily:FONT }}>📍 {modal.location}</span>
            <span style={{ color:TEXT_SEC, fontSize:13, fontFamily:FONT }}>{modal.urgency}</span>
          </div>
          <a href={`https://wa.me/${modal.phone.replace(/[^0-9]/g,"")}?text=${encodeURIComponent("السلام عليكم، شفت طلبك على تطبيق صدقة وحاب أساعدك في: "+modal.title)}`} target="_blank" rel="noopener noreferrer" style={{ width:"100%", background:"#25D366", border:"none", borderRadius:30, padding:"14px", color:"#fff", fontSize:16, fontWeight:800, cursor:"pointer", fontFamily:FONT, display:"flex", alignItems:"center", justifyContent:"center", gap:8, textDecoration:"none", marginBottom:10, boxSizing:"border-box" }}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="#fff"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.872.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
            تواصل عبر واتساب
          </a>
          <button onClick={() => setModal(null)} style={{ width:"100%", background:"transparent", border:`1.5px solid ${BORDER}`, borderRadius:30, padding:"13px", color:TEXT_SEC, fontSize:15, fontWeight:700, cursor:"pointer", fontFamily:FONT }}>إغلاق</button>
        </Sheet>
      )}

      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Tajawal:wght@400;500;700;800;900&display=swap');
        * { box-sizing: border-box; margin: 0; padding: 0; }
        textarea::placeholder, input::placeholder { color: #BBBBBB; }
        input:focus, textarea:focus { border-color: #4CC92260 !important; outline: none; }
        ::-webkit-scrollbar { width: 4px; }
        ::-webkit-scrollbar-track { background: transparent; }
        ::-webkit-scrollbar-thumb { background: #E0E0E0; border-radius: 4px; }
        @keyframes fadeInUp { from { opacity:0; transform:translateX(-50%) translateY(20px); } to { opacity:1; transform:translateX(-50%) translateY(0); } }
        @keyframes slideIn { from { transform:translateX(100%); } to { transform:translateX(0); } }
        @keyframes su { from { transform:translateY(100%); } to { transform:translateY(0); } }
      `}</style>
    </div>
  );
}